import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import PromptDialog from '../components/PromptDialog';

const ConfirmDialogContext = createContext(null);

/**
 * AP / TP genelinde `window.confirm` / `prompt` yerine kullanılır.
 */
export function ConfirmDialogProvider({ children }) {
  const [confirmCfg, setConfirmCfg] = useState(null);
  const [promptCfg, setPromptCfg] = useState(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setConfirmCfg({
        title: options.title ?? 'Onay',
        message: options.message ?? '',
        variant: options.variant ?? 'danger',
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        iconClass: options.iconClass,
        confirmButtonClassName: options.confirmButtonClassName,
        _resolve: resolve,
      });
    });
  }, []);

  /** `window.prompt` yerine; çözüm: girilen metin veya iptalde `null`. */
  const promptText = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setPromptCfg({
        title: options.title ?? 'Giriş',
        message: options.message,
        placeholder: options.placeholder,
        defaultValue: options.defaultValue ?? '',
        minLength: options.minLength ?? 0,
        multiline: options.multiline !== false,
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        _resolve: resolve,
      });
    });
  }, []);

  const finishConfirm = useCallback((value) => {
    setConfirmCfg((s) => {
      if (!s) return null;
      queueMicrotask(() => s._resolve?.(value));
      return null;
    });
  }, []);

  const finishPrompt = useCallback((value) => {
    setPromptCfg((s) => {
      if (!s) return null;
      queueMicrotask(() => s._resolve?.(value));
      return null;
    });
  }, []);

  const ctxValue = useMemo(() => ({ confirm, promptText }), [confirm, promptText]);

  return (
    <ConfirmDialogContext.Provider value={ctxValue}>
      {children}
      {confirmCfg && (
        <ConfirmDialog
          open
          title={confirmCfg.title}
          message={confirmCfg.message}
          variant={confirmCfg.variant}
          confirmLabel={confirmCfg.confirmLabel ?? 'Tamam'}
          cancelLabel={confirmCfg.cancelLabel ?? 'İptal'}
          iconClass={confirmCfg.iconClass}
          confirmButtonClassName={confirmCfg.confirmButtonClassName}
          onCancel={() => finishConfirm(false)}
          onConfirm={async () => {
            finishConfirm(true);
          }}
        />
      )}
      {promptCfg && (
        <PromptDialog
          open
          title={promptCfg.title}
          message={promptCfg.message}
          placeholder={promptCfg.placeholder}
          defaultValue={promptCfg.defaultValue}
          minLength={promptCfg.minLength}
          multiline={promptCfg.multiline}
          confirmLabel={promptCfg.confirmLabel ?? 'Tamam'}
          cancelLabel={promptCfg.cancelLabel ?? 'İptal'}
          onCancel={() => finishPrompt(null)}
          onSubmit={async (val) => {
            finishPrompt(val);
          }}
        />
      )}
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error(
      'useConfirmDialog yalnızca ConfirmDialogProvider içinde kullanılabilir (ör. index.js).',
    );
  }
  return ctx;
}
